import { ComponentType, SVGProps, useEffect, useMemo } from 'react';

import { useNavigate, useRouterState } from '@tanstack/react-router';
import {
    ArrowRightLeft,
    BookOpen,
    ClipboardCheck,
    FolderOpen,
    FolderTree,
    MessageSquare,
    Plane,
    Search,
    Users,
    Wrench,
} from 'lucide-react';

import { useFlag, useProduct } from '@/lib/api';
import { ProductType } from '@/lib/types';

import { hasOnlyTransition360Roles } from '@/features/auth/utils';
import { useCustomerFeedbackFormFlag } from '@/features/FeedbackForm/useCustomerFeedbackFormFlag';
import { useGroundTruthAccess } from '@/features/GroundTruthHub/useGroundTruthAccess';

import { useHybridSorting } from './useHybridSorting';

import { useMaintenanceReviewGuard } from './useMaintenanceReviewGuard';
import { Permission, usePermissions } from './usePermissions';
import { useTransition360Access } from './useTransition360Access';

type MenuItem = {
    title: string;
    icon: ComponentType<SVGProps<SVGSVGElement>>;
    href: string;
    active: boolean;
    testId?: string;
    /** Renders a BETA pill when the sidebar is expanded, an orange dot when collapsed. */
    isBeta?: boolean;
};
// TODO: Refactor these someday; we can probably separate product flag logic from menu construction
export const useMenu = () => {
    const router = useRouterState();
    const navigate = useNavigate();

    // Example usage: Products
    const { data: isSbAgentEnabled, isLoading: isLoadingSbAgent } = useProduct(
        ProductType.sbAgent,
    );
    const { data: isEndOfLeaseEnabled, isLoading: isLoadingEndOfLease } =
        useProduct(ProductType.endOfLease);
    const {
        data: isMaintenanceReviewEnabled,
        isLoading: isLoadingMaintenanceReview,
    } = useProduct(ProductType.maintenanceReview);
    const { data: isIataBinderEnabled, isLoading: isLoadingIataBinder } =
        useProduct(ProductType.iataBinder);
    const { data: isAsset360Enabled, isLoading: isLoadingAsset360 } =
        useProduct(ProductType.asset360);

    const isLoadingMenu = useMemo(() => {
        return (
            isLoadingSbAgent ||
            isLoadingEndOfLease ||
            isLoadingMaintenanceReview ||
            isLoadingIataBinder ||
            isLoadingAsset360
        );
    }, [
        isLoadingSbAgent,
        isLoadingEndOfLease,
        isLoadingMaintenanceReview,
        isLoadingIataBinder,
        isLoadingAsset360,
    ]);
    const { isEnabled: isAssetBinderEnabled } = useHybridSorting();
    const { data: isDocumentSearchEnabled } = useFlag('document_search');
    const { hasPermission, userProfile } = usePermissions();
    const {
        canAccess: canAccessTransition360,
        isShownInMenu: isTransition360ShownInMenu,
        isFetching: isFetchingTransition360,
    } = useTransition360Access();
    const isTransition360OnlyUser = hasOnlyTransition360Roles(
        userProfile?.roles ?? [],
    );
    // Gated solely by the single GT access seam (Rules of Hooks: call it
    // unconditionally at the top, alongside the other hooks).
    const { canAccess: canAccessGroundTruth } = useGroundTruthAccess();
    const feedbackFormFlag = useCustomerFeedbackFormFlag();

    //Redirect guard if maintenance review is disabled
    //Secure by default - only allow access after confirming feature is enabled
    useMaintenanceReviewGuard({
        isMaintenanceReviewEnabled: isMaintenanceReviewEnabled ?? false,
        isLoading: isLoadingMaintenanceReview,
        path: router.location.pathname,
    });

    // Handle automatic redirection from root path to appropriate feature
    useEffect(() => {
        // Only redirect if we're exactly on the root path and have feature data loaded
        if (
            router.location.pathname === '/' &&
            (isSbAgentEnabled !== undefined ||
                isEndOfLeaseEnabled !== undefined ||
                isMaintenanceReviewEnabled !== undefined ||
                isIataBinderEnabled !== undefined ||
                isAsset360Enabled !== undefined)
        ) {
            if (isTransition360OnlyUser) {
                if (canAccessTransition360) {
                    navigate({ to: '/transition-360' });
                    return;
                }
                // Cached products can predate T360, so wait for the refetch before falling back
                if (isFetchingTransition360) return;
            }
            // Priority order: asset360 > endOfLease > maintenanceReview > iataBinder
            if (isAsset360Enabled) {
                navigate({ to: '/asset_collection' });
                return;
            }
            if (isEndOfLeaseEnabled) {
                navigate({ to: '/lease' });
                return;
            }
            if (isMaintenanceReviewEnabled) {
                navigate({ to: '/aircrafts' });
                return;
            }
            if (isIataBinderEnabled) {
                navigate({ to: '/iata-binder' });
                return;
            }
        }
    }, [
        router.location.pathname,
        isAsset360Enabled,
        isSbAgentEnabled,
        isEndOfLeaseEnabled,
        isMaintenanceReviewEnabled,
        isIataBinderEnabled,
        isTransition360OnlyUser,
        canAccessTransition360,
        isFetchingTransition360,
        navigate,
    ]);

    const isActive = (href: string) => {
        return href === '/'
            ? router.location.pathname === '/'
            : router.location.pathname.startsWith(href);
    };

    const createMenuItem = ({
        title,
        icon,
        href,
        testId,
        isBeta,
    }: {
        title: string;
        icon: any;
        href: string;
        testId?: string;
        isLoading?: boolean;
        isBeta?: boolean;
    }) => ({
        title,
        icon,
        href,
        testId,
        isBeta,
        active: isActive(href),
    });
    // We only want to hide Statistics & Domain Knowledge if Maintenance Review is the only enabled product
    const isOnlyMaintenanceReviewEnabled = useMemo(() => {
        return (
            isMaintenanceReviewEnabled &&
            !isSbAgentEnabled &&
            !isEndOfLeaseEnabled &&
            !isAsset360Enabled
        );
    }, [
        isMaintenanceReviewEnabled,
        isSbAgentEnabled,
        isEndOfLeaseEnabled,
        isAsset360Enabled,
    ]);

    // Create menu sections
    const toolsMenuItems: MenuItem[] = [];
    const analysisMenuItems: MenuItem[] = [];
    const systemMenuItems: MenuItem[] = [];

    // Tools section - Active features
    if (isAsset360Enabled && hasPermission(Permission.ReadAsset360)) {
        toolsMenuItems.push(
            createMenuItem({
                title: 'Asset 360',
                icon: BookOpen,
                href: '/asset_collection',
                testId: 'MenuItem__toolsAsset360',
            }),
        );
    }

    // Directly under Asset 360: the binder is the same asset's documents, seen the new way.
    if (
        isAsset360Enabled &&
        isAssetBinderEnabled &&
        hasPermission(Permission.ReadAsset360)
    ) {
        toolsMenuItems.push(
            createMenuItem({
                title: 'Asset Binder',
                icon: FolderTree,
                href: '/asset-binder',
                testId: 'MenuItem__toolsAssetBinder',
                isBeta: true,
            }),
        );
    }

    if (isEndOfLeaseEnabled && hasPermission([Permission.ManageEndOfLease])) {
        toolsMenuItems.push(
            createMenuItem({
                title: 'End of Lease Projects',
                icon: Plane,
                href: '/lease',
                testId: 'MenuItem__toolsEndOfLease',
            }),
        );
    }

    if (isMaintenanceReviewEnabled) {
        toolsMenuItems.push(
            createMenuItem({
                title: 'Maintenance Review',
                icon: Wrench,
                href: '/aircrafts',
                testId: 'MenuItem__toolsAircraftReview',
            }),
        );
    }

    if (isIataBinderEnabled && hasPermission([Permission.ManageEndOfLease])) {
        toolsMenuItems.push(
            createMenuItem({
                title: 'IATA Binder',
                icon: FolderTree,
                href: '/iata-binder',
                testId: 'MenuItem__toolsIataBinder',
            }),
        );
    }

    if (isTransition360ShownInMenu) {
        toolsMenuItems.push(
            createMenuItem({
                title: 'Transition360',
                icon: ArrowRightLeft,
                href: '/transition-360',
                testId: 'MenuItem__toolsTransition360',
            }),
        );
    }

    // Analysis section
    if (hasPermission([Permission.ViewAnalytics])) {
        analysisMenuItems.push(
            createMenuItem({
                title: 'Document Repository',
                icon: FolderOpen,
                href: '/documents',
                testId: 'MenuItem__documentsList',
            }),
        );
        if (isDocumentSearchEnabled) {
            analysisMenuItems.push(
                createMenuItem({
                    title: 'Document Search',
                    icon: Search,
                    href: '/meta-search',
                    testId: 'MenuItem__documentsSearch',
                }),
            );
        }
    }

    // System section
    if (hasPermission([Permission.ViewTeam])) {
        systemMenuItems.push(
            createMenuItem({
                title: 'Team',
                icon: Users,
                href: '/teams',
                testId: 'MenuItem__systemTeams',
            }),
        );
    }

    if (canAccessGroundTruth) {
        systemMenuItems.push(
            createMenuItem({
                title: 'Ground Truth',
                icon: ClipboardCheck,
                href: '/ground-truth',
                testId: 'MenuItem__systemGroundTruth',
            }),
        );
    }

    // The profile menu carries feedback while the form flag is on.
    if (feedbackFormFlag.isResolved && !feedbackFormFlag.isEnabled) {
        systemMenuItems.push(
            createMenuItem({
                title: 'Feedback',
                icon: MessageSquare,
                href: '/feedback',
                testId: 'MenuItem__feedback',
            }),
        );
    }

    // Build menu sections
    const menuSections = [];

    // Add Tools section only if there are enabled tools
    if (toolsMenuItems.length > 0) {
        menuSections.push({
            name: 'Tools',
            menuItems: toolsMenuItems,
        });
    }

    // Add Analysis section
    if (analysisMenuItems.length > 0) {
        menuSections.push({
            name: 'Analysis',
            menuItems: analysisMenuItems,
        });
    }

    // Add System section
    if (systemMenuItems.length > 0) {
        menuSections.push({
            name: 'System',
            menuItems: systemMenuItems,
        });
    }

    return {
        menuSections,
        isLoadingMenu,
    };
};
